import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
} from '@roger/ui-components';
import { ShieldCheck } from 'lucide-react';
import type { SigningAttestation } from './attestations';
import {
  MIN_ESIGN_PASSWORD_LENGTH,
  type ESignatureCredentialInput,
} from './esignatureCredentialApi';
import { useESignatureCredential } from './useESignatureCredential';
import { ESignatureResetFields } from './ESignatureResetFields';

/** Local wall-clock time of a lockout expiry, e.g. "2:45 PM". */
function formatLockoutTime(isoTimestamp: string): string {
  const parsed = new Date(isoTimestamp);
  if (Number.isNaN(parsed.getTime())) {
    return 'shortly';
  }
  return parsed.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface AttestationDialogProps {
  open: boolean;
  /** When null (e.g. still loading from API), body shows a loading state. */
  attestation: SigningAttestation | null;
  signerName: string | null;
  signerCredentials?: string | null;
  /** Signer's email. Gives the browser password manager an account to key on. */
  signerEmail?: string | null;
  isLoading?: boolean;
  actionLabel?: string;
  /**
   * Performs the signature. Return a message to show inside this dialog when it
   * fails, or null/undefined on success.
   *
   * The dialog stays open on failure so the typed password is not thrown away,
   * which means a refusal has to render in here: the panels display their own
   * errors in the page body, behind this modal's overlay, where nobody can read
   * them.
   */
  onConfirm: (
    credential: ESignatureCredentialInput
  ) => void | Promise<string | null | void>;
  onCancel: () => void;
}

/**
 * Pre-sign confirmation dialog.
 *
 * The signer reads and acknowledges the attestation text and re-enters their
 * e-signature password before the actual `/sign` call fires. The client sends
 * `attestation_version` and the password for server validation.
 *
 * A clinician who has no e-signature password yet is asked to create one here
 * rather than being blocked. That is how existing clinicians get a credential
 * without a rollout window where nobody can sign.
 *
 * The fields live in a real `<form>` with a readonly username input and
 * `autoComplete` set, so the browser password manager offers to save the
 * password and can fill it next time. Clinicians sign many charts a day; without
 * this they would forget and reset it constantly.
 */
export const AttestationDialog: React.FC<AttestationDialogProps> = ({
  open,
  attestation,
  signerName,
  signerCredentials,
  signerEmail,
  isLoading = false,
  actionLabel = 'Sign',
  onConfirm,
  onCancel,
}) => {
  const {
    status,
    isLoading: statusLoading,
    error: statusError,
    refresh,
    revalidate,
    isStale,
  } = useESignatureCredential();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  const isFirstTimeSetup = status !== null && !status.has_credential;
  const lockedUntil = status?.locked_until ?? null;

  // Never leave a typed password sitting in state after the dialog closes.
  useEffect(() => {
    if (!open) {
      setPassword('');
      setConfirmPassword('');
      setValidationError(null);
      setSubmitError(null);
      setIsResetting(false);
    }
  }, [open]);

  // Recheck every time the dialog opens. A first signature creates the
  // credential server-side, so a status cached from before that would keep
  // showing the create form and every later signature would be refused with
  // "already set" and no field to enter it in. Silent, so a signer who already
  // has one does not see a loading flash on a dialog they open all day.
  //
  // Submitting is blocked while it is in flight: the visible fields still come
  // from the pre-recheck status, so a fast click would send the shape those
  // fields imply and be refused with a message contradicting what was shown.
  const [isRechecking, setIsRechecking] = useState(false);
  useEffect(() => {
    if (!open) {
      return;
    }
    let current = true;
    setIsRechecking(true);
    void revalidate().finally(() => {
      if (current) {
        setIsRechecking(false);
      }
    });
    return () => {
      current = false;
    };
  }, [open, revalidate]);

  const displayName = useMemo(
    () => [signerName, signerCredentials].filter(Boolean).join(', '),
    [signerName, signerCredentials]
  );

  const canSubmit =
    Boolean(attestation) &&
    !isLoading &&
    !statusLoading &&
    !isRechecking &&
    // A failed recheck means the visible fields may be for the wrong mode, and
    // submitting would be refused with a message no field on screen can satisfy.
    !isStale &&
    lockedUntil === null &&
    password.length > 0 &&
    (!isFirstTimeSetup || confirmPassword.length > 0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    // The dialog stays on the page, so the browser must not navigate. Chrome
    // still offers to save the password: it detects the submit and then sees the
    // form leave the DOM when the dialog closes on success.
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    if (isFirstTimeSetup && password !== confirmPassword) {
      setValidationError('Those passwords do not match.');
      return;
    }
    if (isFirstTimeSetup && password.length < MIN_ESIGN_PASSWORD_LENGTH) {
      // Mirrors the server rule so a too-short password is caught before the
      // request, rather than the dialog closing and losing what was typed.
      setValidationError(
        `Use at least ${MIN_ESIGN_PASSWORD_LENGTH} characters for your e-signature password.`
      );
      return;
    }
    setValidationError(null);
    setSubmitError(null);
    const message = await onConfirm(
      isFirstTimeSetup
        ? { new_esign_password: password }
        : { esign_password: password }
    );
    if (typeof message === 'string' && message.length > 0) {
      setSubmitError(message);
    }
  };

  const handleResetComplete = async () => {
    // The credential still exists, so the dialog stays in enter-password mode;
    // refresh anyway so a lockout cleared by the reset is reflected.
    await refresh();
    setIsResetting(false);
    setPassword('');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={next => !next && !isLoading && onCancel()}
    >
      <DialogContent className='sm:max-w-[560px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-foreground'>
            <ShieldCheck className='h-5 w-5 text-primary' />
            Confirm Electronic Signature
          </DialogTitle>
          <DialogDescription className='space-y-3 pt-2 text-sm'>
            <div className='rounded-md border border-border/70 bg-muted/30 px-3 py-3 text-foreground'>
              {attestation ? (
                <p className='whitespace-pre-wrap leading-relaxed'>
                  {attestation.text}
                </p>
              ) : (
                <p className='text-muted-foreground text-sm'>
                  Loading attestation…
                </p>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {isResetting ? (
          <ESignatureResetFields
            signerEmail={signerEmail ?? null}
            onDone={handleResetComplete}
            onCancel={() => setIsResetting(false)}
          />
        ) : statusError ? (
          // Without the status we cannot tell whether to ask for an existing
          // password or to walk them through creating one, and guessing wrong
          // leaves them submitting a request that can only fail.
          <>
            <p className='py-2 text-sm text-destructive'>
              {statusError.message}
            </p>
            <DialogFooter>
              <Button type='button' variant='outline' onClick={onCancel}>
                Cancel
              </Button>
              <Button type='button' onClick={() => void refresh()}>
                Try again
              </Button>
            </DialogFooter>
          </>
        ) : statusLoading ? (
          // Which fields to show depends on whether this signer already has a
          // password. Waiting avoids flashing "enter your password" at someone
          // who is about to be asked to create one.
          <>
            <p className='py-2 text-sm text-muted-foreground'>
              Checking your e-signature setup…
            </p>
            <DialogFooter>
              <Button type='button' variant='outline' onClick={onCancel}>
                Cancel
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={e => void handleSubmit(e)} className='space-y-4'>
            {/* Readonly, but a real input so the password manager has a
                username to file the entry under. Also doubles as the
                "signing as" line the dialog used to render as plain text. */}
            <div className='space-y-1.5'>
              <Label htmlFor='esign-username'>Signing as</Label>
              <Input
                id='esign-username'
                name='username'
                type='text'
                autoComplete='username'
                readOnly
                value={signerEmail ?? displayName}
                className='bg-muted/40'
              />
              {signerEmail && displayName ? (
                <p className='text-xs text-muted-foreground'>{displayName}</p>
              ) : null}
            </div>

            {isFirstTimeSetup ? (
              <>
                <p className='text-sm text-muted-foreground'>
                  Create an e-signature password. You will enter it each time
                  you sign. It is separate from your Roger sign-in password.
                </p>
                <div className='space-y-1.5'>
                  <Label htmlFor='esign-new-password'>
                    New e-signature password
                  </Label>
                  <Input
                    id='esign-new-password'
                    name='new-password'
                    type='password'
                    autoComplete='new-password'
                    autoFocus
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                  />
                </div>
                <div className='space-y-1.5'>
                  <Label htmlFor='esign-confirm-password'>
                    Confirm e-signature password
                  </Label>
                  <Input
                    id='esign-confirm-password'
                    name='confirm-new-password'
                    type='password'
                    autoComplete='new-password'
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <div className='space-y-1.5'>
                <Label htmlFor='esign-password'>E-signature password</Label>
                <Input
                  id='esign-password'
                  name='password'
                  type='password'
                  autoComplete='current-password'
                  autoFocus
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <button
                  type='button'
                  className='text-xs text-primary hover:underline'
                  onClick={() => setIsResetting(true)}
                >
                  Forgot your e-signature password?
                </button>
              </div>
            )}

            {isStale ? (
              <p className='text-sm text-destructive'>
                Could not confirm your e-signature setup.{' '}
                <button
                  type='button'
                  className='underline'
                  onClick={() => void refresh()}
                >
                  Try again
                </button>
                .
              </p>
            ) : null}

            {lockedUntil ? (
              // Otherwise a locked-out clinician finds out one refused attempt
              // at a time, with an enabled button that cannot work.
              <p className='text-sm text-destructive'>
                Too many incorrect attempts. Signing is locked until{' '}
                {formatLockoutTime(lockedUntil)}. Reset your e-signature
                password to unlock it now.
              </p>
            ) : null}

            {validationError ? (
              <p className='text-sm text-destructive'>{validationError}</p>
            ) : null}

            {submitError ? (
              // The server refused the signature (wrong password, lockout, a
              // failed save). It has to be shown here rather than in the panel
              // behind the overlay, or the button just appears to do nothing.
              <p className='text-sm text-destructive'>{submitError}</p>
            ) : null}

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={onCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={!canSubmit}>
                {isLoading ? 'Signing…' : actionLabel}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
